/**
 * Video Slider — "See FELCO in Action"
 *
 * Behaviours:
 *  - Video paused by default, play icon centred.
 *  - On hover → auto-play (muted), play icon hides.
 *  - On mouse-leave → pause, play icon re-appears.
 *  - Click play button → also starts playback.
 *  - Mute / Unmute toggle (per card).
 *  - Chevron prev/next navigation with visibility based on scroll position.
 */

(function () {
  'use strict';

  var SCROLL_AMOUNT = 300;
  var _videoModalDocumentClickBound = false;

  // ==== Video Pod Logic ====

  function initVideoPods(scope) {
    var pods = (scope || document).querySelectorAll('.video-pod');
    pods.forEach(initPod);
  }

  function initPod(pod) {
    var media = pod.querySelector('.video-pod__media');
    var video = pod.querySelector('.video-pod__video');
    var playBtn = pod.querySelector('.video-pod__play');
    var muteBtn = pod.querySelector('.video-pod__mute');

    if (!video || !media) return;

    // Ensure video starts paused & muted
    video.pause();
    video.muted = true;

    // iOS Safari won't paint a video frame without user interaction even with
    // preload="metadata". Seeking to a tiny non-zero time after metadata loads
    // forces it to decode and display the first frame as a thumbnail.
    function seekToFirstFrame() {
      if (video.currentTime === 0) {
        video.currentTime = 0.001;
      }
    }
    if (video.readyState >= 1) {
      seekToFirstFrame();
    } else {
      video.addEventListener('loadedmetadata', seekToFirstFrame, { once: true });
    }

    // ---- Hover behaviour ----
    media.addEventListener('mouseenter', function () {
      video.play().catch(function () {});
      pod.classList.add('is-playing');
    });

    media.addEventListener('mouseleave', function () {
      video.pause();
      pod.classList.remove('is-playing');
    });

    // ---- Play button click (mobile / a11y) ----
    if (playBtn) {
      playBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (video.paused) {
          video.play().catch(function () {});
          pod.classList.add('is-playing');
        } else {
          video.pause();
          pod.classList.remove('is-playing');
        }
      });
    }

    // ---- Mute / Unmute ----
    if (muteBtn) {
      muteBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (video.muted) {
          video.muted = false;
          pod.classList.add('is-unmuted');
        } else {
          video.muted = true;
          pod.classList.remove('is-unmuted');
        }
      });
    }

    // Touch support: toggle play on tap
    media.addEventListener('touchend', function (e) {
      if (e.changedTouches && e.changedTouches.length === 1) {
        if (e.target.closest('button') || e.target.closest('a')) return;
        if (video.paused) {
          video.play().catch(function () {});
          pod.classList.add('is-playing');
        } else {
          video.pause();
          pod.classList.remove('is-playing');
        }
      }
    });

    // Click pod to open modal
    media.addEventListener('click', function (e) {
      if (e.target.closest('button') || e.target.closest('a')) return;
      var section = pod.closest('.video-slider-section');
      if (section) {
        var index = parseInt(pod.getAttribute('data-video-index'), 10);
        openModal(section, index);
      }
    });
  }

  // ==== Chevron Navigation Logic ====

  function initChevrons(scope) {
    var wrappers = (scope || document).querySelectorAll('.video-slider__slider-wrapper');
    wrappers.forEach(function (wrapper) {
      var prevBtn = wrapper.querySelector('[data-slider-prev]');
      var nextBtn = wrapper.querySelector('[data-slider-next]');
      var viewport = wrapper.querySelector('[data-horizontal-slider-viewport]');
      var track = wrapper.querySelector('[data-horizontal-slider-track]');

      if (!prevBtn || !nextBtn || !viewport || !track) return;

      function updateChevrons() {
        var scrollLeft = viewport.scrollLeft;
        var maxScroll = track.scrollWidth - viewport.clientWidth;
        var hasOverflow = track.scrollWidth > viewport.clientWidth + 1;

        if (hasOverflow) {
          prevBtn.classList.toggle('is-visible', scrollLeft > 5);
          nextBtn.classList.toggle('is-visible', scrollLeft < maxScroll - 5);
        } else {
          prevBtn.classList.remove('is-visible');
          nextBtn.classList.remove('is-visible');
        }
      }

      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        viewport.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
      });

      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        viewport.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
      });

      viewport.addEventListener('scroll', updateChevrons, { passive: true });
      window.addEventListener('resize', updateChevrons);

      // Initial check
      updateChevrons();
    });
  }

  // ==== Initialise ====

  function initAll(scope) {
    initVideoPods(scope);
    initChevrons(scope);
    initModals(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  // ==== Modal Logic ====

  function getPodsForSection(section) {
    return Array.from(section.querySelectorAll('.video-pod[data-video-index]'));
  }

  function openModal(section, index) {
    var modal = section.querySelector('[data-video-modal]');
    if (!modal) return;

    modal._section = section;
    modal._currentIndex = index;

    populateModal(modal, section, index);

    // Ensure a page-level overlay is present so the dim background
    // is always visible and clickable regardless of stacking contexts.
    var pageOverlay = document.querySelector('[data-video-modal-page-overlay]');
    if (!pageOverlay) {
      pageOverlay = document.createElement('div');
      pageOverlay.setAttribute('data-video-modal-page-overlay', '');
      pageOverlay.className = 'video-modal__overlay video-modal__page-overlay';
      document.body.appendChild(pageOverlay);
      pageOverlay.addEventListener('click', function () {
        closeModal(modal);
      });
    }

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    var vid = modal.querySelector('[data-video-modal-video]');
    if (vid) {
      vid.play().catch(function () {});
      modal.classList.add('is-modal-playing');
    }
  }

  function closeModal(modal) {
    var vid = modal.querySelector('[data-video-modal-video]');
    if (vid) {
      vid.pause();
      vid.removeAttribute('src');
      vid.load();
    }
    modal.classList.remove('is-open', 'is-modal-playing', 'is-modal-unmuted', 'is-modal-loading');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Remove the page-level overlay if present
    var pageOverlay = document.querySelector('[data-video-modal-page-overlay]');
    if (pageOverlay && pageOverlay.parentNode) {
      pageOverlay.parentNode.removeChild(pageOverlay);
    }

    var desc = modal.querySelector('[data-video-modal-description]');
    var showMoreBtn = modal.querySelector('[data-video-modal-show-more]');
    if (desc) desc.classList.remove('is-expanded');
    if (showMoreBtn) {
      showMoreBtn.textContent = 'Show more';
      showMoreBtn.style.display = 'none';
    }
  }

  function populateModal(modal, section, index) {
    var pods = getPodsForSection(section);
    var pod = pods[index];
    if (!pod) return;

    var videoSrc = pod.getAttribute('data-video-src') || '';
    var creatorName = pod.getAttribute('data-creator-name') || '';
    var description = pod.getAttribute('data-card-description') || '';

    var vid = modal.querySelector('[data-video-modal-video]');
    var creatorEl = modal.querySelector('[data-video-modal-creator]');
    var descEl = modal.querySelector('[data-video-modal-description]');
    var showMoreBtn = modal.querySelector('[data-video-modal-show-more]');

    if (vid) {
      vid.pause();
      vid.muted = !modal.classList.contains('is-modal-unmuted');
      // mark as loading so controls remain hidden until metadata is available
      modal.classList.add('is-modal-loading');
      vid.src = videoSrc;
      vid.load();
      vid.play().catch(function () {});
      modal.classList.add('is-modal-playing');

      // Size controls to match actual rendered video width
      var controls = modal.querySelector('[data-video-modal-controls]');
      if (controls) {
        function syncControlsWidth() {
          if (vid.videoWidth && vid.videoHeight) {
            var renderedWidth = vid.clientHeight * (vid.videoWidth / vid.videoHeight);
            renderedWidth = Math.min(renderedWidth, vid.clientWidth);
            controls.style.width = Math.round(renderedWidth) + 'px';
          } else {
            controls.style.width = '';
          }
        }

        function onLoadedMetadata() {
          // remove loading state so controls become visible
          modal.classList.remove('is-modal-loading');
          syncControlsWidth();
          vid.removeEventListener('loadedmetadata', onLoadedMetadata);
        }

        vid.addEventListener('loadedmetadata', onLoadedMetadata);
        vid.addEventListener('resize', syncControlsWidth);
        requestAnimationFrame(syncControlsWidth);
      }
    }

    if (creatorEl) {
      creatorEl.textContent = creatorName ? '@ ' + creatorName : '';
    }

    if (descEl) {
      descEl.textContent = description;
      descEl.classList.remove('is-expanded');
    }

    if (showMoreBtn) {
      showMoreBtn.textContent = 'Show more';
      showMoreBtn.style.display = 'none';
      requestAnimationFrame(function () {
        if (descEl && descEl.scrollHeight > descEl.clientHeight + 1) {
          showMoreBtn.style.display = '';
        } else {
          showMoreBtn.style.display = 'none';
        }
      });
    }

    var prevBtn = modal.querySelector('[data-video-modal-prev]');
    var nextBtn = modal.querySelector('[data-video-modal-next]');
    if (prevBtn) prevBtn.disabled = (index <= 0);
    if (nextBtn) nextBtn.disabled = (index >= pods.length - 1);

    modal._currentIndex = index;
  }

  function initModals(scope) {
    var modals = (scope || document).querySelectorAll('[data-video-modal]');
    modals.forEach(function (modal) {
      var closeBtns = modal.querySelectorAll('[data-video-modal-close]');
      closeBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          closeModal(modal);
        });
      });

      var overlay = modal.querySelector('[data-video-modal-overlay]');
      if (overlay) {
        overlay.addEventListener('click', function () {
          closeModal(modal);
        });
      }

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) {
          closeModal(modal);
        }
      });

      var playPauseBtn = modal.querySelector('[data-video-modal-play]');
      var vid = modal.querySelector('[data-video-modal-video]');
      if (playPauseBtn && vid) {
        playPauseBtn.addEventListener('click', function () {
          if (vid.paused) {
            vid.play().catch(function () {});
            modal.classList.add('is-modal-playing');
          } else {
            vid.pause();
            modal.classList.remove('is-modal-playing');
          }
        });
      }

      var muteBtn = modal.querySelector('[data-video-modal-mute]');
      if (muteBtn && vid) {
        muteBtn.addEventListener('click', function () {
          if (vid.muted) {
            vid.muted = false;
            modal.classList.add('is-modal-unmuted');
          } else {
            vid.muted = true;
            modal.classList.remove('is-modal-unmuted');
          }
        });
      }

      var prevBtn = modal.querySelector('[data-video-modal-prev]');
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          var section = modal._section;
          var idx = modal._currentIndex;
          if (section && idx > 0) {
            populateModal(modal, section, idx - 1);
          }
        });
      }

      var nextBtn = modal.querySelector('[data-video-modal-next]');
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          var section = modal._section;
          var idx = modal._currentIndex;
          var pods = section ? getPodsForSection(section) : [];
          if (section && idx < pods.length - 1) {
            populateModal(modal, section, idx + 1);
          }
        });
      }

      var showMoreBtn = modal.querySelector('[data-video-modal-show-more]');
      var descEl = modal.querySelector('[data-video-modal-description]');
      if (showMoreBtn && descEl) {
        showMoreBtn.addEventListener('click', function () {
          var isExpanded = descEl.classList.toggle('is-expanded');
          showMoreBtn.textContent = isExpanded ? 'Show less' : 'Show more';
        });
      }
    });

    // Robust fallback: close any open video modal when clicking/tapping outside the container
    if (!_videoModalDocumentClickBound) {
      document.addEventListener('click', function (e) {
        var openModal = document.querySelector('[data-video-modal].is-open');
        if (!openModal) return;
        if (e.target.closest('.video-modal__container')) return;
        closeModal(openModal);
      }, true);
      _videoModalDocumentClickBound = true;
    }
  }
})();
